<template>
  <div>
    <div class="page-header">
      <div>
        <h5 class="page-title">Accounts</h5>
        <p class="page-sub">CRM view of customers and their activity</p>
      </div>
      <div class="header-actions">
        <div class="export-wrap" ref="exportWrap">
          <button class="btn-export" @click="exportMenuOpen = !exportMenuOpen" :disabled="exporting">
            <font-awesome-icon icon="download" /> Export <font-awesome-icon icon="chevron-down" style="font-size:10px" />
          </button>
          <div v-if="exportMenuOpen" class="export-menu">
            <button class="export-opt" @click="openExportModal('csv')"><font-awesome-icon icon="file-csv" class="me-2" />Export CSV</button>
            <button class="export-opt" @click="openExportModal('pdf')"><font-awesome-icon icon="file-pdf" class="me-2" />Export PDF</button>
          </div>
        </div>
        <button v-if="activeTab === 'categories' && permission?.edit_crm_accounts" class="btn-primary" @click="openCreateCategory">
          <font-awesome-icon icon="plus" class="me-1" /> New Category
        </button>
      </div>
    </div>

    <!-- Tabs -->
    <div class="tabs">
      <button class="tab-btn" :class="{ 'tab-btn--active': activeTab === 'accounts' }" @click="activeTab = 'accounts'">
        <font-awesome-icon icon="building" class="me-1" /> Accounts
      </button>
      <button class="tab-btn" :class="{ 'tab-btn--active': activeTab === 'categories' }" @click="activeTab = 'categories'; fetchCategories()">
        <font-awesome-icon icon="tags" class="me-1" /> Categories
      </button>
    </div>

    <!-- ── Accounts Tab ──────────────────────────────────────────────────── -->
    <template v-if="activeTab === 'accounts'">
      <div class="filter-bar">
        <div class="per-page-wrap">
          <label class="per-page-label">Show</label>
          <select class="per-page-select" v-model.number="length" @change="page = 1; fetchAccounts()">
            <option v-for="n in [5,10,20,50,100,200,500,1000]" :key="n" :value="n">{{ n }}</option>
          </select>
          <span class="per-page-label">per page</span>
        </div>
        <input v-model="search" @input="debouncedFetch" class="filter-input" placeholder="Search by name, email..." />
        <select v-model="filterCategory" @change="page=1; fetchAccounts()" class="filter-select">
          <option value="">All Categories</option>
          <option v-for="c in categories" :key="c.id" :value="c.id">{{ c.name }}</option>
        </select>
        <select v-model="filterLocation" @change="page=1; fetchAccounts()" class="filter-select">
          <option value="">All Locations</option>
          <option v-for="loc in locations" :key="loc.id" :value="loc.id">{{ loc.name }}</option>
        </select>
        <input type="date" v-model="dateFrom" class="filter-input" style="max-width:160px" />
        <input type="date" v-model="dateTo" class="filter-input" style="max-width:160px" />
        <button class="btn-apply" @click="applyDateFilter">Apply</button>
        <button class="btn-clear" @click="clearDateFilter" v-if="dateFrom || dateTo">Clear</button>
      </div>

      <div class="card">
        <div v-if="loading" class="loading-state"><div class="spinner"></div><p>Loading accounts...</p></div>
        <div v-else-if="!accounts.length" class="empty-state">
          <font-awesome-icon icon="building" class="empty-icon" />
          <p>No accounts found</p>
          <span>Accounts are created when leads are converted</span>
        </div>
        <table v-else class="data-table">
          <thead>
            <tr><th>Account Name</th><th>Category</th><th>Location</th><th>Primary Contact</th><th>Email</th><th>Phone</th><th>Opportunities</th><th>Open Cases</th><th></th></tr>
          </thead>
          <tbody>
            <tr v-for="a in accounts" :key="a.id">
              <td class="name-col">{{ a.customer_name }}</td>
              <td>
                <span v-if="a.category" class="cat-badge" :style="{ background: a.category.color + '22', color: a.category.color }">
                  {{ a.category.name }}
                </span>
                <span v-else class="muted">—</span>
              </td>
              <td>{{ a.location?.name || '—' }}</td>
              <td>{{ a.primary_contact_name ?? '—' }}</td>
              <td>{{ a.email ?? '—' }}</td>
              <td>{{ a.phone_no ?? '—' }}</td>
              <td>{{ a.opportunities_count ?? 0 }}</td>
              <td>{{ a.open_cases_count ?? 0 }}</td>
              <td>
                <div class="row-actions">
                  <button v-if="permission?.edit_crm_accounts" class="btn-link btn-link--edit" @click="openEdit(a)">Edit</button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-if="meta" class="pagination-bar">
          <span class="pagination-info">{{ meta.from ?? 1 }}–{{ meta.to ?? meta.total }} of {{ meta.total }} records</span>
          <div class="pagination-controls">
            <button class="pg-btn" :disabled="meta.current_page <= 1" @click="changePage(1)">«</button>
            <button class="pg-btn" :disabled="meta.current_page <= 1" @click="changePage(meta.current_page - 1)">‹</button>
            <div class="pg-pages">
              <button v-for="p in pageRange" :key="p" class="pg-num" :class="{ 'pg-num--active': p === meta.current_page }" @click="changePage(p)">{{ p }}</button>
            </div>
            <button class="pg-btn" :disabled="meta.current_page >= meta.last_page" @click="changePage(meta.current_page + 1)">›</button>
            <button class="pg-btn" :disabled="meta.current_page >= meta.last_page" @click="changePage(meta.last_page)">»</button>
          </div>
        </div>
      </div>

      <!-- Edit Account Modal -->
      <div class="modal-overlay" :class="{ open: modalOpen }" @click.self="closeModal">
        <div class="modal-box">
          <div class="modal-header">
            <span class="modal-title">Edit Account</span>
            <button class="modal-close" @click="closeModal"><font-awesome-icon icon="times" /></button>
          </div>
          <form @submit.prevent="submit">
            <div class="modal-body">
              <div class="form-group form-group--full">
                <label class="form-label">Account Name</label>
                <input v-model="form.customer_name" class="form-input" />
              </div>
              <div class="form-row">
                <div class="form-group">
                  <label class="form-label">Email</label>
                  <input v-model="form.email" type="email" class="form-input" />
                </div>
                <div class="form-group">
                  <label class="form-label">Phone</label>
                  <input v-model="form.phone_no" class="form-input" />
                </div>
              </div>
              <div class="form-group form-group--full">
                <label class="form-label">Category</label>
                <select v-model="form.category_id" class="form-input">
                  <option value="">— None —</option>
                  <option v-for="c in categories" :key="c.id" :value="c.id">{{ c.name }}</option>
                </select>
              </div>
              <div class="form-group form-group--full">
                <label class="form-label">Location</label>
                <select v-model="form.location_id" class="form-input">
                  <option value="">— None —</option>
                  <option v-for="loc in locations" :key="loc.id" :value="loc.id">{{ loc.name }}</option>
                </select>
              </div>
              <div class="form-group form-group--full">
                <label class="form-label">Address</label>
                <textarea v-model="form.address" class="form-input" rows="2"></textarea>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn-ghost" @click="closeModal">Cancel</button>
              <button type="submit" class="btn-primary" :disabled="submitting">{{ submitting ? 'Saving…' : 'Save Changes' }}</button>
            </div>
          </form>
        </div>
      </div>
    </template>

    <!-- ── Categories Tab ────────────────────────────────────────────────── -->
    <template v-if="activeTab === 'categories'">
      <div class="card">
        <div v-if="catLoading" class="loading-state"><div class="spinner"></div><p>Loading categories...</p></div>
        <div v-else-if="!categories.length" class="empty-state">
          <font-awesome-icon icon="tags" class="empty-icon" />
          <p>No categories yet</p>
          <span>Create categories to organise your accounts</span>
        </div>
        <table v-else class="data-table">
          <thead>
            <tr><th>Colour</th><th>Name</th><th>Description</th><th>Accounts</th><th></th></tr>
          </thead>
          <tbody>
            <tr v-for="c in categories" :key="c.id">
              <td><span class="color-dot" :style="{ background: c.color }"></span></td>
              <td class="name-col">
                <span class="cat-badge" :style="{ background: c.color + '22', color: c.color }">{{ c.name }}</span>
              </td>
              <td class="muted">{{ c.description || '—' }}</td>
              <td>{{ c.customers_count ?? 0 }}</td>
              <td>
                <div class="row-actions">
                  <button v-if="permission?.edit_crm_accounts" class="btn-link btn-link--edit" @click="openEditCategory(c)">Edit</button>
                  <button v-if="permission?.edit_crm_accounts" class="btn-link btn-link--del" @click="catDeleteTarget = c">Delete</button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Create / Edit Category Modal -->
      <div class="modal-overlay" :class="{ open: catModalOpen }" @click.self="closeCatModal">
        <div class="modal-box modal-box--sm">
          <div class="modal-header">
            <span class="modal-title">{{ catEditing ? 'Edit Category' : 'New Category' }}</span>
            <button class="modal-close" @click="closeCatModal"><font-awesome-icon icon="times" /></button>
          </div>
          <form @submit.prevent="submitCategory">
            <div class="modal-body">
              <div class="form-group form-group--full">
                <label class="form-label">Name <span class="req">*</span></label>
                <input v-model="catForm.name" class="form-input" required />
              </div>
              <div class="form-group form-group--full">
                <label class="form-label">Colour</label>
                <div class="color-row">
                  <input v-model="catForm.color" type="color" class="color-picker" />
                  <div class="color-presets">
                    <button v-for="p in colorPresets" :key="p" type="button"
                      class="color-preset" :style="{ background: p }"
                      :class="{ 'color-preset--active': catForm.color === p }"
                      @click="catForm.color = p">
                    </button>
                  </div>
                </div>
                <div class="cat-preview">
                  <span class="cat-badge" :style="{ background: catForm.color + '22', color: catForm.color }">
                    {{ catForm.name || 'Preview' }}
                  </span>
                </div>
              </div>
              <div class="form-group form-group--full">
                <label class="form-label">Description</label>
                <textarea v-model="catForm.description" class="form-input" rows="2" placeholder="Optional description..."></textarea>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn-ghost" @click="closeCatModal">Cancel</button>
              <button type="submit" class="btn-primary" :disabled="submitting">{{ submitting ? 'Saving…' : (catEditing ? 'Save Changes' : 'Create Category') }}</button>
            </div>
          </form>
        </div>
      </div>

      <!-- Delete Category Confirm -->
      <div class="modal-overlay" :class="{ open: !!catDeleteTarget }" @click.self="catDeleteTarget = null">
        <div class="modal-box modal-box--sm">
          <div class="modal-header">
            <span class="modal-title">Delete Category</span>
            <button class="modal-close" @click="catDeleteTarget = null"><font-awesome-icon icon="times" /></button>
          </div>
          <div class="modal-body">
            <p>Delete <strong>{{ catDeleteTarget?.name }}</strong>? Accounts in this category will become uncategorised.</p>
          </div>
          <div class="modal-footer">
            <button class="btn-ghost" @click="catDeleteTarget = null">Cancel</button>
            <button class="btn-danger" @click="deleteCategory" :disabled="submitting">{{ submitting ? 'Deleting…' : 'Delete' }}</button>
          </div>
        </div>
      </div>
    </template>

    <!-- Column-Picker Export Modal -->
    <transition name="fade">
      <div v-if="showExportModal" class="col-picker-backdrop" @click.self="showExportModal=false">
        <div class="col-picker-modal">
          <div class="col-picker-header">
            <span>Choose Columns to Export <span class="col-picker-badge">{{ selectedExportCols.length }}</span></span>
            <button class="col-picker-close" @click="showExportModal=false"><font-awesome-icon icon="times"/></button>
          </div>
          <div class="col-picker-body">
            <div class="col-picker-actions">
              <button class="col-picker-link" @click="selectedExportCols=exportColumnDefs.map(c=>c.key)">Select All</button>
              <button class="col-picker-link" @click="selectedExportCols=[]">Deselect All</button>
            </div>
            <div class="col-picker-grid">
              <label v-for="col in exportColumnDefs" :key="col.key" class="col-picker-item">
                <input type="checkbox" :value="col.key" v-model="selectedExportCols" />
                {{ col.label }}
              </label>
            </div>
          </div>
          <div class="col-picker-footer">
            <button class="btn-ghost" @click="showExportModal=false">Cancel</button>
            <button class="btn-primary" :disabled="!selectedExportCols.length||exporting" @click="doExport">
              <font-awesome-icon v-if="exporting" icon="spinner" spin class="me-1"/>
              Export {{ pendingExportFmt.toUpperCase() }}
            </button>
          </div>
        </div>
      </div>
    </transition>

  </div>
</template>

<script>
import axios from 'axios';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { useToast } from 'vue-toastification';
import { useStore } from 'vuex';

const COLOR_PRESETS = ['#004AAD','#0f766e','#166534','#7c3aed','#b45309','#be185d','#dc2626','#374151','#0369a1','#9a3412'];

export default {
  setup() { return { toast: useToast(), store: useStore() }; },
  computed: {
    permission() { return this.store.state.getCurrentUserPermission; },
    pageRange() {
      if (!this.meta) return [];
      const cur = this.meta.current_page, last = this.meta.last_page;
      const range = [];
      for (let i = Math.max(1, cur - 2); i <= Math.min(last, cur + 2); i++) range.push(i);
      return range;
    },
  },
  data() {
    return {
      activeTab: 'accounts',
      // accounts
      accounts: [], meta: null, loading: false, submitting: false,
      search: '', filterCategory: '', filterLocation: '', page: 1, length: 5, modalOpen: false, editing: null,
      dateFrom: '', dateTo: '', exportMenuOpen: false, exporting: false,
      showExportModal: false, pendingExportFmt: 'csv', selectedExportCols: [],
      exportColumnDefs: [
        { key: 'customer_name',       label: 'Account Name',   pdfWidth: 130 },
        { key: 'category',            label: 'Category',       pdfWidth: 90 },
        { key: 'location',            label: 'Location',       pdfWidth: 90 },
        { key: 'primary_contact',     label: 'Primary Contact',pdfWidth: 120 },
        { key: 'email',               label: 'Email',          pdfWidth: 140 },
        { key: 'phone',               label: 'Phone',          pdfWidth: 90 },
        { key: 'opportunities_count', label: 'Opportunities',  pdfWidth: 60 },
        { key: 'open_cases_count',    label: 'Open Cases',     pdfWidth: 41 },
      ],
      form: { customer_name:'', email:'', phone_no:'', address:'', category_id:'', location_id:'' },
      debounceTimer: null,
      // categories
      categories: [], catLoading: false,
      // locations
      locations: [],
      catModalOpen: false, catEditing: null, catDeleteTarget: null,
      catForm: { name:'', color:'#004AAD', description:'' },
      colorPresets: COLOR_PRESETS,
    };
  },
  mounted() { this.fetchAccounts(); this.fetchCategories(); this.fetchLocations(); document.addEventListener('click', this.onOutsideClick); },
  beforeUnmount() { document.removeEventListener('click', this.onOutsideClick); },
  methods: {
    /* ── Accounts ── */
    async fetchAccounts() {
      this.loading = true;
      try {
        const { data } = await axios.get('/crm/accounts', { params: { search: this.search, category_id: this.filterCategory || undefined, location_id: this.filterLocation || undefined, from: this.dateFrom || undefined, to: this.dateTo || undefined, page: this.page, length: this.length } });
        this.accounts = data.data?.data ?? data.data ?? data ?? [];
        this.meta     = data.data?.meta ?? data.meta ?? null;
      } catch { this.toast.error('Failed to load accounts'); }
      finally { this.loading = false; }
    },
    debouncedFetch() { clearTimeout(this.debounceTimer); this.debounceTimer = setTimeout(() => { this.page = 1; this.fetchAccounts(); }, 400); },
    changePage(p) { this.page = p; this.fetchAccounts(); },
    applyDateFilter() { this.page = 1; this.fetchAccounts(); },
    clearDateFilter() { this.dateFrom = ''; this.dateTo = ''; this.page = 1; this.fetchAccounts(); },
    openExportModal(format) {
      this.exportMenuOpen     = false;
      this.pendingExportFmt   = format;
      this.selectedExportCols = this.exportColumnDefs.map(c => c.key);
      this.showExportModal    = true;
    },
    getRowValue(r, key) {
      switch (key) {
        case 'customer_name':       return r.customer_name ?? '—';
        case 'category':            return r.category?.name ?? '—';
        case 'location':            return r.location?.name ?? '—';
        case 'primary_contact':     return r.primary_contact_name ?? '—';
        case 'email':               return r.email ?? '—';
        case 'phone':               return r.phone_no ?? '—';
        case 'opportunities_count': return String(r.opportunities_count ?? 0);
        case 'open_cases_count':    return String(r.open_cases_count ?? 0);
        default:                    return '—';
      }
    },
    async doExport() {
      if (!this.selectedExportCols.length) return;
      this.exporting = true; this.showExportModal = false;
      try {
        const params = { page: 1, per_page: 10000 };
        if (this.search)          params.search      = this.search;
        if (this.filterCategory)  params.category_id = this.filterCategory;
        if (this.filterLocation)  params.location_id = this.filterLocation;
        if (this.dateFrom)        params.from        = this.dateFrom;
        if (this.dateTo)          params.to          = this.dateTo;
        const { data } = await axios.get('/crm/accounts', { params });
        const rows = data.data?.data ?? data.data ?? data ?? [];
        const cols = this.exportColumnDefs.filter(c => this.selectedExportCols.includes(c.key));
        const headers = cols.map(c => c.label);
        const body    = rows.map(r => cols.map(c => this.getRowValue(r, c.key)));
        const date    = new Date().toISOString().slice(0, 10);
        if (this.pendingExportFmt === 'csv') {
          const csv  = [headers, ...body].map(r => r.map(v => `"${String(v).replace(/"/g,'""')}"`).join(',')).join('\n');
          const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
          const url  = URL.createObjectURL(blob);
          const a    = document.createElement('a');
          a.href = url; a.download = `accounts-${date}.csv`; a.click(); URL.revokeObjectURL(url);
        } else {
          const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
          const usable = 761; const totalW = cols.reduce((s,c)=>s+c.pdfWidth,0); const scale = usable/totalW;
          const colStyles = {}; cols.forEach((c,i)=>{ colStyles[i]={cellWidth:c.pdfWidth*scale}; });
          doc.setFontSize(18); doc.setFont('helvetica','bold'); doc.setTextColor(26,43,69); doc.text('Accounts',40,46);
          doc.setFontSize(9); doc.setFont('helvetica','normal'); doc.setTextColor(122,144,176);
          doc.text(`Exported ${new Date().toLocaleDateString('en-GB')}   ·   ${rows.length} record(s)`,40,62);
          doc.autoTable({ startY:78, head:[headers], body,
            styles:{fontSize:7.5,cellPadding:3.5,textColor:[26,43,69],overflow:'ellipsize'},
            headStyles:{fillColor:[0,74,173],textColor:255,fontStyle:'bold',fontSize:7.5},
            alternateRowStyles:{fillColor:[248,250,252]}, columnStyles:colStyles, tableWidth:usable, margin:{left:40,right:40},
            didDrawPage(d){ doc.setFontSize(8); doc.setTextColor(192,202,214); doc.text(`Page ${d.pageNumber}`,doc.internal.pageSize.width/2,doc.internal.pageSize.height-16,{align:'center'}); },
          });
          doc.save(`accounts-${date}.pdf`);
        }
      } catch { this.toast?.error('Export failed. Please try again.'); }
      finally { this.exporting = false; }
    },
    onOutsideClick(e) {
      if (this.$refs.exportWrap && !this.$refs.exportWrap.contains(e.target)) this.exportMenuOpen = false;
    },
    openEdit(a) {
      this.editing = a.id;
      this.form = { customer_name: a.customer_name??'', email: a.email??'', phone_no: a.phone_no??'', address: a.address??'', category_id: a.category_id??'', location_id: a.location_id??'' };
      this.modalOpen = true;
    },
    closeModal() { this.modalOpen = false; },
    async submit() {
      this.submitting = true;
      try {
        await axios.put(`/crm/accounts/${this.editing}`, this.form);
        this.toast.success('Account updated'); this.closeModal(); this.fetchAccounts();
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed to save'); }
      finally { this.submitting = false; }
    },

    async fetchLocations() {
      try {
        const { data } = await axios.get('/admin/location/active');
        this.locations = data.data ?? [];
      } catch { /* optional */ }
    },

    /* ── Categories ── */
    async fetchCategories() {
      this.catLoading = true;
      try {
        const { data } = await axios.get('/crm/account-categories');
        this.categories = data.data ?? [];
      } catch { this.toast.error('Failed to load categories'); }
      finally { this.catLoading = false; }
    },
    openCreateCategory() { this.catEditing = null; this.catForm = { name:'', color:'#004AAD', description:'' }; this.catModalOpen = true; },
    openEditCategory(c) { this.catEditing = c.id; this.catForm = { name: c.name, color: c.color, description: c.description??'' }; this.catModalOpen = true; },
    closeCatModal() { this.catModalOpen = false; },
    async submitCategory() {
      this.submitting = true;
      try {
        if (this.catEditing) { await axios.put(`/crm/account-categories/${this.catEditing}`, this.catForm); this.toast.success('Category updated'); }
        else                 { await axios.post('/crm/account-categories', this.catForm); this.toast.success('Category created'); }
        this.closeCatModal(); this.fetchCategories();
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed to save'); }
      finally { this.submitting = false; }
    },
    async deleteCategory() {
      this.submitting = true;
      try {
        await axios.delete(`/crm/account-categories/${this.catDeleteTarget.id}`);
        this.toast.success('Category deleted'); this.catDeleteTarget = null; this.fetchCategories(); this.fetchAccounts();
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed to delete'); }
      finally { this.submitting = false; }
    },
  }
};
</script>

<style scoped>
.page-header{display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:16px}
.page-title{font-size:20px;font-weight:700;color:#1a2b45;margin:0 0 3px}
.page-sub{font-size:13px;color:#7a90b0;margin:0}
/* tabs */
.tabs{display:flex;gap:4px;margin-bottom:16px;border-bottom:2px solid #e8edf5;padding-bottom:0}
.tab-btn{background:none;border:none;padding:9px 18px;font-size:13px;font-weight:600;color:#7a90b0;cursor:pointer;border-bottom:2px solid transparent;margin-bottom:-2px;display:inline-flex;align-items:center;gap:6px;transition:color .15s,border-color .15s}
.tab-btn:hover{color:#1a2b45}
.tab-btn--active{color:#004AAD;border-bottom-color:#004AAD}
/* filter */
.filter-bar{display:flex;gap:10px;margin-bottom:16px;flex-wrap:wrap}
.filter-input{padding:9px 13px;border:1.5px solid #dde5f0;border-radius:8px;font-size:13px;color:#1a2b45;flex:1;min-width:180px;outline:none}
.filter-input:focus{border-color:#004AAD}
.filter-select{padding:9px 12px;border:1.5px solid #dde5f0;border-radius:8px;font-size:13px;color:#1a2b45;background:#fff;outline:none}
/* buttons */
.btn-primary{background:#004AAD;color:#fff;border:none;padding:9px 18px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;gap:6px}
.btn-primary:hover{background:#003b8e}
.btn-primary:disabled{opacity:.55;cursor:not-allowed}
.btn-ghost{background:#f4f7fb;color:#3d5068;border:1px solid #e4eaf2;padding:8px 16px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer}
.btn-danger{background:#e03535;color:#fff;border:none;padding:8px 16px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer}
.btn-danger:disabled{opacity:.55;cursor:not-allowed}
/* card / table */
.card{background:#fff;border-radius:12px;border:1.5px solid #e8edf5;overflow:hidden}
.loading-state,.empty-state{display:flex;flex-direction:column;align-items:center;justify-content:center;padding:56px 24px;color:#7a90b0;gap:8px}
.empty-icon{font-size:36px;opacity:.4}
.spinner{width:28px;height:28px;border:3px solid #e8edf5;border-top-color:#004AAD;border-radius:50%;animation:spin .7s linear infinite}
@keyframes spin{to{transform:rotate(360deg)}}
.data-table{width:100%;border-collapse:collapse;font-size:13px}
.data-table th{background:#f4f7fb;font-weight:600;color:#5a6a85;padding:11px 14px;text-align:left;white-space:nowrap}
.data-table td{padding:11px 14px;border-top:1px solid #f0f4fa;color:#1a2b45;vertical-align:middle}
.data-table tbody tr:hover{background:#f9fbff}
.name-col{font-weight:600}
.muted{color:#a0aec0;font-size:12.5px}
.row-actions{display:flex;gap:8px}
.btn-link{background:none;border:none;font-size:12.5px;cursor:pointer;padding:0;font-weight:600}
.btn-link--edit{color:#004AAD}
.btn-link--del{color:#e03535}
.per-page-wrap{display:flex;align-items:center;gap:8px;flex-shrink:0}
.per-page-label{font-size:13px;color:#5a6a85;white-space:nowrap}
.per-page-select{padding:7px 10px;border:1.5px solid #dde5f0;border-radius:8px;font-size:13px;color:#1a2b45;background:#fff;outline:none;cursor:pointer}
.per-page-select:focus{border-color:#004AAD}
.pagination-bar{display:flex;align-items:center;justify-content:space-between;padding:12px 16px;border-top:1px solid #f0f4fa;flex-wrap:wrap;gap:10px}
.pagination-info{font-size:12.5px;color:#7a90b0}
.pagination-controls{display:flex;align-items:center;gap:4px}
.pg-btn{padding:6px 10px;border:1.5px solid #dde5f0;border-radius:7px;background:#fff;color:#1a2b45;font-size:13px;cursor:pointer}
.pg-btn:disabled{opacity:.4;cursor:not-allowed}
.pg-pages{display:flex;gap:3px}
.pg-num{width:30px;height:30px;border:1.5px solid #dde5f0;border-radius:7px;background:#fff;color:#1a2b45;font-size:12.5px;cursor:pointer;display:flex;align-items:center;justify-content:center}
.pg-num:hover{border-color:#004AAD;color:#004AAD}
.pg-num--active{background:#004AAD;border-color:#004AAD;color:#fff}
/* category badge */
.cat-badge{display:inline-block;font-size:11.5px;font-weight:700;border-radius:20px;padding:3px 10px}
.color-dot{display:inline-block;width:14px;height:14px;border-radius:50%;vertical-align:middle}
/* colour picker */
.color-row{display:flex;align-items:center;gap:12px;margin-bottom:8px}
.color-picker{width:42px;height:36px;border:1.5px solid #dde5f0;border-radius:8px;padding:2px;cursor:pointer;background:none}
.color-presets{display:flex;gap:6px;flex-wrap:wrap}
.color-preset{width:24px;height:24px;border-radius:50%;border:2px solid transparent;cursor:pointer;padding:0;transition:transform .1s,border-color .1s}
.color-preset:hover{transform:scale(1.15)}
.color-preset--active{border-color:#1a2b45}
.cat-preview{margin-top:6px}
/* modal */
.modal-overlay{position:fixed;inset:0;z-index:1050;display:flex;align-items:center;justify-content:center;background:rgba(15,25,50,.45);backdrop-filter:blur(3px);opacity:0;pointer-events:none;transition:opacity .2s;padding:16px}
.modal-overlay.open{opacity:1;pointer-events:all}
.modal-box{background:#fff;border-radius:14px;width:100%;max-width:560px;box-shadow:0 20px 60px rgba(0,0,0,.14);animation:slideUp .2s ease;max-height:90vh;overflow-y:auto}
.modal-box--sm{max-width:420px}
@keyframes slideUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
.modal-header{display:flex;align-items:center;justify-content:space-between;padding:18px 22px;border-bottom:1px solid #e4eaf2}
.modal-title{font-size:16px;font-weight:700;color:#1a2b45}
.modal-close{width:28px;height:28px;border-radius:7px;border:1px solid #dde5f0;background:transparent;cursor:pointer;font-size:14px;color:#7a90b0;display:flex;align-items:center;justify-content:center}
.modal-body{padding:20px 22px}
.modal-footer{display:flex;justify-content:flex-end;gap:10px;padding:16px 22px;border-top:1px solid #f0f4fa}
.form-row{display:grid;grid-template-columns:1fr 1fr;gap:14px}
.form-group{display:flex;flex-direction:column;gap:5px;margin-bottom:14px}
.form-group--full{grid-column:1/-1}
.form-label{font-size:12.5px;font-weight:600;color:#1a2b45}
.form-input{padding:9px 12px;border:1.5px solid #dde5f0;border-radius:8px;font-size:13px;color:#1a2b45;outline:none;width:100%;font-family:inherit}
.form-input:focus{border-color:#004AAD;box-shadow:0 0 0 3px rgba(0,74,173,.07)}
.req{color:#e03535}
.me-1{margin-right:4px}
.header-actions{display:flex;align-items:center;gap:10px}
.export-wrap{position:relative}
.btn-export{background:#f4f7fb;color:#3d5068;border:1.5px solid #e4eaf2;padding:9px 14px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;gap:5px;white-space:nowrap}
.btn-export:hover{background:#e8edf5}
.btn-export:disabled{opacity:.55;cursor:not-allowed}
.export-menu{position:absolute;top:calc(100% + 6px);right:0;background:#fff;border:1.5px solid #e4eaf2;border-radius:10px;box-shadow:0 6px 20px rgba(0,0,0,.1);z-index:200;min-width:160px;overflow:hidden}
.export-opt{display:flex;align-items:center;width:100%;padding:10px 14px;background:none;border:none;font-size:13px;color:#1a2b45;cursor:pointer;text-align:left}
.export-opt:hover{background:#f4f7fb}
.btn-apply{background:#004AAD;color:#fff;border:none;padding:9px 14px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer}
.btn-apply:hover{background:#003b8e}
.btn-clear{background:#f4f7fb;color:#3d5068;border:1.5px solid #e4eaf2;padding:9px 14px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer}
.btn-clear:hover{background:#e8edf5}
.me-2{margin-right:6px}
.col-picker-backdrop{position:fixed;inset:0;background:rgba(10,20,40,.45);z-index:1100;display:flex;align-items:center;justify-content:center}
.col-picker-modal{background:#fff;border-radius:14px;width:480px;max-width:95vw;box-shadow:0 8px 40px rgba(0,74,173,.18);overflow:hidden}
.col-picker-header{display:flex;align-items:center;justify-content:space-between;padding:18px 22px 14px;border-bottom:1.5px solid #eaeff6;font-size:15px;font-weight:700;color:#1a2b45}
.col-picker-badge{background:#004AAD;color:#fff;border-radius:20px;padding:1px 9px;font-size:12px;font-weight:700;margin-left:8px}
.col-picker-close{background:none;border:none;cursor:pointer;color:#7a90b0;font-size:16px;line-height:1}
.col-picker-body{padding:16px 22px}
.col-picker-actions{display:flex;gap:14px;margin-bottom:12px}
.col-picker-link{background:none;border:none;color:#004AAD;font-size:13px;font-weight:600;cursor:pointer;padding:0}
.col-picker-link:hover{text-decoration:underline}
.col-picker-grid{display:grid;grid-template-columns:1fr 1fr;gap:8px 16px}
.col-picker-item{display:flex;align-items:center;gap:8px;font-size:13.5px;color:#1a2b45;cursor:pointer}
.col-picker-item input[type=checkbox]{width:15px;height:15px;accent-color:#004AAD;cursor:pointer}
.col-picker-footer{display:flex;justify-content:flex-end;gap:10px;padding:14px 22px 18px;border-top:1.5px solid #eaeff6}
.fade-enter-active,.fade-leave-active{transition:opacity .18s ease}
.fade-enter,.fade-leave-to{opacity:0}
</style>
